import {
  useV2CreateLineOfBusinessMutation,
  useV2UpdateLineOfBusinessMutation,
  useV2DeleteLineOfBusinessMutation,
  useV2ChangeLineOfBusinessPositionByIdMutation,
} from "@generated";
import type { V2LineOfBusinessResource } from "@generated";
import type { LobFormValues } from "../types";

// The `account` relationship is inferred from auth server-side, so we omit it
// and cast (same pattern as the Currencies/BusinessUnits MSPAs).
export const buildLobData = (
  values: LobFormValues,
  id?: string
): V2LineOfBusinessResource =>
  ({
    type: "line-of-businesses",
    ...(id ? { id: Number(id) } : {}),
    attributes: { name: values.name, code: values.code },
  } as unknown as V2LineOfBusinessResource);

// Reorder via the dedicated /moves endpoint (acts_as_list set_list_position),
// same shape as the service-category move.
const movePositionData = (
  id: string,
  position: number
): V2LineOfBusinessResource =>
  ({
    type: "line-of-businesses",
    id: Number(id),
    attributes: { position },
  } as unknown as V2LineOfBusinessResource);

export const useLineOfBusinessMutations = () => {
  const [create] = useV2CreateLineOfBusinessMutation();
  const [update] = useV2UpdateLineOfBusinessMutation();
  const [destroy] = useV2DeleteLineOfBusinessMutation();
  const [move] = useV2ChangeLineOfBusinessPositionByIdMutation();

  return {
    create: (values: LobFormValues) =>
      create({ body: { data: buildLobData(values) } }).unwrap(),
    update: (id: string, values: LobFormValues) =>
      update({
        id: Number(id),
        body: { data: buildLobData(values, id) },
      }).unwrap(),
    archive: (id: string) => destroy({ id: Number(id) }).unwrap(),
    move: (id: string, position: number) =>
      move({
        id: Number(id),
        body: { data: movePositionData(id, position) },
      }).unwrap(),
    restore: (id: string, name: string) =>
      update({
        id: Number(id),
        body: {
          data: {
            type: "line-of-businesses",
            id: Number(id),
            attributes: { name, active: true },
          } as unknown as V2LineOfBusinessResource,
        },
      }).unwrap(),
  };
};

export default useLineOfBusinessMutations;
